import type { PuzzleType } from '../types.ts';
import type { EdgeDef, MapDef, RoomDef, RoomKind } from './types.ts';

/**
 * The four heists of the launch (GDD §10). Room and edge lists were verified by script when
 * the GDD was written and are verified again by `validateMap` in the tests.
 */

type Pool = readonly PuzzleType[];

function room(id: string, floor: number, kind: RoomKind, pool: Pool, extraLoot = 0): RoomDef {
  return { id, floor, kind, pool, extraLoot };
}

/** "a-b" is a free passage, "a-bg" a guarded corridor. */
function edges(spec: string): EdgeDef[] {
  return spec
    .split(' ')
    .filter(Boolean)
    .map((e) => {
      const guarded = e.endsWith('g');
      const [a, b] = e.replace(/g$/, '').split('-');
      if (!a || !b) throw new Error(`bad edge spec: ${e}`);
      return { a, b, guarded };
    });
}

function ropes(spec: string): [string, string][] {
  return spec
    .split(' ')
    .filter(Boolean)
    .map((r) => {
      const [a, b] = r.split('-');
      if (!a || !b) throw new Error(`bad rope spec: ${r}`);
      return [a, b];
    });
}

// ---- Puzzle pools by kind of room --------------------------------------------------------
const OFFICE: Pool = ['safe', 'password', 'lock'];
const ARCHIVE: Pool = ['lock', 'cameras', 'password'];
const HALL: Pool = ['cameras', 'laser', 'lock'];
const COUNTER: Pool = ['lock', 'panel', 'safe'];
const SERVICE: Pool = ['lock', 'panel'];
const POWER: Pool = ['panel', 'password', 'cameras'];
const CORRIDOR: Pool = ['laser', 'cameras', 'panel'];
const STRONGROOM: Pool = ['safe', 'lock', 'laser'];
const VAULT_PRE: Pool = ['laser', 'safe', 'panel', 'lock'];
const ROOF: Pool = ['lock', 'panel', 'cameras'];
const SHOWCASE: Pool = ['laser', 'cameras', 'password'];

export const BANK: MapDef = {
  id: 'bank',
  baseLoot: 300,
  timerPermille: 1000,
  guardDelta: 0,
  secondPuzzleBonusPermille: 0,
  dimVisionFloor: null,
  tunnels: false,
  rooms: [
    room('bank.skylight', 2, 'roof', ROOF),
    room('bank.directorOffice', 1, 'optional', OFFICE, 50),
    room('bank.archive', 1, 'optional', ARCHIVE, 40),
    room('bank.gallery', 1, 'normal', HALL),
    room('bank.atrium', 0, 'normal', HALL),
    room('bank.tellers', 0, 'normal', COUNTER),
    room('bank.serviceYard', 0, 'service', SERVICE),
    room('bank.countingRoom', 0, 'optional', COUNTER, 60),
    room('bank.powerRoom', -1, 'normal', POWER),
    room('bank.armoredCorridor', -1, 'normal', CORRIDOR),
    room('bank.safeDeposit', -1, 'optional', STRONGROOM, 80),
    room('bank.vault', -1, 'vault', VAULT_PRE),
    room('bank.sewerTunnel', -1, 'sewer', SERVICE),
  ],
  edges: edges(
    'bank.skylight-bank.galleryg bank.gallery-bank.directorOffice bank.gallery-bank.archive ' +
      'bank.gallery-bank.atriumg bank.atrium-bank.tellers bank.atrium-bank.serviceYard ' +
      'bank.atrium-bank.powerRoomg bank.tellers-bank.countingRoom bank.tellers-bank.armoredCorridorg ' +
      'bank.tellers-bank.vaultg bank.serviceYard-bank.powerRoom bank.powerRoom-bank.armoredCorridor ' +
      'bank.armoredCorridor-bank.safeDeposit bank.armoredCorridor-bank.vaultg ' +
      'bank.sewerTunnel-bank.powerRoom bank.sewerTunnel-bank.serviceYard',
  ),
  ropeLinks: ropes('bank.skylight-bank.atrium bank.gallery-bank.powerRoom'),
  entrances: { roof: 'bank.skylight', sewer: 'bank.sewerTunnel', service: 'bank.serviceYard' },
  vault: 'bank.vault',
};

export const MUSEUM: MapDef = {
  id: 'museum',
  baseLoot: 360,
  timerPermille: 1000,
  guardDelta: 0,
  secondPuzzleBonusPermille: 0,
  // The masked ball on the main floor distracts the guards (GDD §10.2).
  dimVisionFloor: 1,
  tunnels: false,
  rooms: [
    room('museum.attic', 2, 'roof', ROOF),
    room('museum.costumeLoft', 2, 'normal', ARCHIVE),
    room('museum.maskRoom', 1, 'optional', SHOWCASE, 50),
    room('museum.ballroom', 1, 'normal', HALL),
    room('museum.portraitGallery', 1, 'normal', SHOWCASE),
    room('museum.grandStair', 0, 'normal', HALL),
    room('museum.ticketOffice', 0, 'optional', COUNTER, 40),
    room('museum.serviceLane', 0, 'service', SERVICE),
    room('museum.restorationLab', 0, 'normal', POWER),
    room('museum.controlRoom', -1, 'normal', POWER),
    room('museum.treasureCrypt', -1, 'vault', VAULT_PRE),
    room('museum.undergroundCanal', -1, 'sewer', SERVICE),
    room('museum.jewelRoom', 1, 'optional', SHOWCASE, 80),
  ],
  edges: edges(
    'museum.attic-museum.costumeLoft museum.costumeLoft-museum.ballroomg museum.ballroom-museum.maskRoom ' +
      'museum.ballroom-museum.portraitGallery museum.ballroom-museum.grandStairg ' +
      'museum.portraitGallery-museum.grandStairg museum.portraitGallery-museum.jewelRoom ' +
      'museum.grandStair-museum.ticketOffice museum.grandStair-museum.serviceLane ' +
      'museum.grandStair-museum.restorationLab museum.grandStair-museum.controlRoomg ' +
      'museum.serviceLane-museum.restorationLab museum.serviceLane-museum.controlRoom ' +
      'museum.restorationLab-museum.treasureCryptg museum.controlRoom-museum.treasureCryptg ' +
      'museum.undergroundCanal-museum.controlRoom museum.undergroundCanal-museum.restorationLab',
  ),
  ropeLinks: ropes(
    'museum.attic-museum.ballroom museum.ballroom-museum.grandStair museum.portraitGallery-museum.controlRoom',
  ),
  entrances: {
    roof: 'museum.attic',
    sewer: 'museum.undergroundCanal',
    service: 'museum.serviceLane',
  },
  vault: 'museum.treasureCrypt',
};

export const VILLA: MapDef = {
  id: 'villa',
  baseLoot: 420,
  timerPermille: 1000,
  // Fewer guards, more contraptions (GDD §10.3).
  guardDelta: -1,
  secondPuzzleBonusPermille: 200,
  dimVisionFloor: null,
  tunnels: false,
  rooms: [
    room('villa.terrace', 2, 'roof', ROOF),
    room('villa.greenhouse', 2, 'optional', ARCHIVE, 40),
    room('villa.wonderSalon', 1, 'normal', SHOWCASE),
    room('villa.library', 1, 'normal', OFFICE),
    room('villa.collectorStudy', 1, 'normal', OFFICE),
    room('villa.statueGallery', 0, 'normal', HALL),
    room('villa.kitchen', 0, 'normal', COUNTER),
    room('villa.serviceEntrance', 0, 'service', SERVICE),
    room('villa.cellar', -1, 'normal', POWER),
    room('villa.controlRoom', -1, 'normal', POWER),
    room('villa.rarityVault', -1, 'vault', VAULT_PRE),
    room('villa.seaCave', -1, 'sewer', SERVICE),
    room('villa.stampRoom', 1, 'optional', OFFICE, 70),
  ],
  edges: edges(
    'villa.terrace-villa.greenhouse villa.terrace-villa.wonderSalong villa.wonderSalon-villa.library ' +
      'villa.wonderSalon-villa.collectorStudy villa.library-villa.stampRoom villa.library-villa.statueGalleryg ' +
      'villa.collectorStudy-villa.statueGalleryg villa.statueGallery-villa.kitchen ' +
      'villa.statueGallery-villa.serviceEntrance villa.kitchen-villa.serviceEntrance ' +
      'villa.statueGallery-villa.cellarg villa.serviceEntrance-villa.cellar villa.cellar-villa.controlRoom ' +
      'villa.cellar-villa.rarityVaultg villa.controlRoom-villa.rarityVaultg villa.seaCave-villa.cellar ' +
      'villa.seaCave-villa.controlRoomg',
  ),
  ropeLinks: ropes('villa.terrace-villa.statueGallery villa.collectorStudy-villa.cellar'),
  entrances: { roof: 'villa.terrace', sewer: 'villa.seaCave', service: 'villa.serviceEntrance' },
  vault: 'villa.rarityVault',
};

export const EXPRESS: MapDef = {
  id: 'express',
  baseLoot: 500,
  timerPermille: 800,
  guardDelta: 0,
  secondPuzzleBonusPermille: 0,
  dimVisionFloor: null,
  tunnels: true,
  rooms: [
    room('express.tailPlatform', 0, 'service', SERVICE),
    room('express.sleeper', 0, 'normal', COUNTER),
    room('express.panoramaCar', 0, 'normal', HALL),
    room('express.diningCar', 0, 'normal', COUNTER),
    room('express.baggageCar', 0, 'normal', ARCHIVE),
    room('express.mailCar', 0, 'vault', VAULT_PRE),
    room('express.sleeperRoof', 1, 'roof', ROOF),
    room('express.diningRoof', 1, 'normal', ROOF),
    room('express.baggageRoof', 1, 'normal', ROOF),
    room('express.kitchen', 0, 'optional', COUNTER, 60),
    room('express.conductorCab', 0, 'optional', OFFICE, 50),
    room('express.undercarriage', -1, 'sewer', SERVICE),
    room('express.presidentialCabin', 0, 'optional', SHOWCASE, 90),
  ],
  edges: edges(
    'express.tailPlatform-express.sleeperg express.sleeper-express.panoramaCarg ' +
      'express.panoramaCar-express.diningCarg express.diningCar-express.baggageCarg ' +
      'express.baggageCar-express.mailCarg express.sleeperRoof-express.diningRoof ' +
      'express.diningRoof-express.baggageRoof express.sleeperRoof-express.panoramaCarg ' +
      'express.diningRoof-express.diningCarg express.baggageRoof-express.baggageCarg ' +
      'express.baggageRoof-express.mailCarg express.diningCar-express.kitchen ' +
      'express.panoramaCar-express.conductorCab express.sleeper-express.presidentialCabin ' +
      'express.undercarriage-express.panoramaCar express.undercarriage-express.diningCar ' +
      'express.tailPlatform-express.sleeperRoofg',
  ),
  ropeLinks: ropes('express.sleeperRoof-express.diningCar express.diningRoof-express.baggageCar'),
  entrances: {
    roof: 'express.sleeperRoof',
    sewer: 'express.undercarriage',
    service: 'express.tailPlatform',
  },
  vault: 'express.mailCar',
};

export const MAPS = { bank: BANK, museum: MUSEUM, villa: VILLA, express: EXPRESS } as const;
